import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import apiClient from '../api/apiClient';
import Swal from 'sweetalert2';
import Modal from 'react-modal';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { getCroppedImg } from '../utils/cropUtils';
import { pdfjs } from 'react-pdf';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

// Set modal root for accessibility
if (typeof document !== 'undefined') {
  Modal.setAppElement('#root');
}

const DataTable = ({ 
  data: initialData, 
  endpoint, 
  onDataChange, 
  currentTab 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(initialData || []);
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({});
  const [foreignKeyData, setForeignKeyData] = useState({});
  const [foreignKeyLoading, setForeignKeyLoading] = useState(false);
  const [foreignKeyErrors, setForeignKeyErrors] = useState({});
  const [displayColumns, setDisplayColumns] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  // Capture Article Modal State
  const [isCaptureModalOpen, setIsCaptureModalOpen] = useState(false);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [pdfImageUrl, setPdfImageUrl] = useState(null);
  const [currentPdffileId, setCurrentPdffileId] = useState(null);
  const imgRef = useRef(null);

  // Image preview modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImageData, setPreviewImageData] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  // Cache loaded PDF document so page navigation doesn't re-download
  const pdfDocRef = useRef(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const pageSizeOptions = [10, 30, 50, { value: 'all', label: 'All' }];

  const booleanColumns = ['is_active', 'is_test', 'is_processed', 'is_deleted'];
  const readOnlyColumns = ['id', 'file_uuid', 'created_at', 'updated_at', 'token', 'reset_password'];
  const hiddenColumns = useMemo(() => [
    'id', 'file_uuid', 'token', 'reset_password', 'file_encoded', 'image_data'
  ], []);
  const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

  const displayBooleanValue = (value) => {
    if (value === true || value === 1 || value === '1') return 'Yes';
    if (value === false || value === 0 || value === '0') return 'No';
    return value;
  };

  const parseBooleanInput = (value) => {
    if (value === 'Yes') return 1;
    if (value === 'No') return 0;
    return value;
  };

  const getForeignKeyName = (column, id) => {
    if (!foreignKeyData[column] || !id) return id;
    const relatedItem = foreignKeyData[column].find(item => item.id === id);
    return relatedItem ? (relatedItem.name || relatedItem.title || relatedItem.label || id) : id;
  };

  // Render a single page from an already-loaded PDF document object
  const renderPageFromDoc = useCallback(async (pdfDoc, pageNum) => {
    const page = await pdfDoc.getPage(pageNum);
    const scale = 2.0;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;

    return canvas.toDataURL('image/png');
  }, []);

  // Fetch PDF binary (with auth header), load via pdf.js, render page to image
  const renderPdfPageToImage = useCallback(async (url, pageNum) => {
    try {
      setIsPdfLoading(true);
      setPdfImageUrl(null);

      let pdfDoc = pdfDocRef.current;

      if (!pdfDoc) {
        const pdfFetchResponse = await fetch(url, {
          headers: {
            'user-Id': localStorage.getItem('authToken') || process.env.REACT_APP_DEFAULT_USER_ID || '',
          }
        });

        if (!pdfFetchResponse.ok) {
          throw new Error(`Failed to download PDF: ${pdfFetchResponse.status}`);
        }

        const pdfArrayBuffer = await pdfFetchResponse.arrayBuffer();
        const loadingTask = pdfjs.getDocument({ data: pdfArrayBuffer });
        pdfDoc = await loadingTask.promise;
        pdfDocRef.current = pdfDoc;
      }

      setNumPages(pdfDoc.numPages);
      const imageUrl = await renderPageFromDoc(pdfDoc, pageNum);
      setPdfImageUrl(imageUrl);

      // Reset crop when page changes
      setCrop(undefined);
      setCompletedCrop(null);
    } catch (error) {
      console.error('Error rendering PDF:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load PDF page. Please check the file exists.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsPdfLoading(false);
    }
  }, [renderPageFromDoc]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    if (file && isAdding) {
      setNewItem(prev => ({
        ...prev,
        name: file.name.replace(/\.[^/.]+$/, "")
      }));
    }
  };

  useEffect(() => {
    setData(initialData || []);
    setCurrentPage(1);
  }, [initialData]);

  useEffect(() => {
    if (data.length > 0) {
      const allColumns = Object.keys(data[0]);
      setDisplayColumns(allColumns.filter(col => !hiddenColumns.includes(col)));
    }
  }, [data, hiddenColumns]);

  useEffect(() => {
    if (data.length === 0) return;

    const fkColumns = Object.keys(data[0])
      .filter(col => col.endsWith('_id') && col !== 'id')
      .filter(col => !(col in foreignKeyData));

    if (fkColumns.length === 0) return;

    const fetchForeignKeyData = async () => {
      setForeignKeyLoading(true);
      const newFkData = {};
      const newErrors = {};
      
      for (const col of fkColumns) {
        let relatedEndpoint = col.replace(/_id$/, '');
        
        if (relatedEndpoint === 'address') {
          relatedEndpoint = 'addresses';
        } else if (relatedEndpoint.endsWith('y')) {
          relatedEndpoint = relatedEndpoint.slice(0, -1) + 'ies';
        } else {
          relatedEndpoint = relatedEndpoint + 's';
        }

        try {
          const response = await apiClient.get(`api/${relatedEndpoint}/v1`);
          newFkData[col] = response.data;
        } catch (error) {
          console.error(`Error fetching data for ${relatedEndpoint}:`, error);
          newErrors[col] = `Could not load ${relatedEndpoint}`;
          newFkData[col] = [];
        }
      }
      
      setForeignKeyData(prev => ({ ...prev, ...newFkData }));
      setForeignKeyErrors(prev => ({ ...prev, ...newErrors }));
      setForeignKeyLoading(false);
    };
    
    fetchForeignKeyData();
  }, [data, foreignKeyData]);

  // ===== CAPTURE ARTICLE FUNCTIONALITY =====
  
  const handleCaptureArticle = async (match) => {
    setCurrentMatch(match);
    setIsCaptureModalOpen(true);
    setCrop(undefined);
    setCompletedCrop(null);
    setPdfImageUrl(null);
    pdfDocRef.current = null;
    
    try {
      const articleResponse = await apiClient.get(`api/articles/v1/${match.article_id}`);
      const article = articleResponse.data;
      const startPage = article.page_number || 1;
      setPageNumber(startPage);
      setCurrentPdffileId(article.pdffile_id);
      
      const pdfResponse = await apiClient.get(`api/pdffiles/v1/${article.pdffile_id}`);
      const pdfFile = pdfResponse.data;
      
      const baseUrl = apiClient.defaults.baseURL || '';
      const downloadUrl = `${baseUrl}/api/pdffiles/download/v1/${pdfFile.file_uuid}`;
      setPdfUrl(downloadUrl);
      
      await renderPdfPageToImage(downloadUrl, startPage);
      
    } catch (error) {
      console.error('Error fetching article or PDF data:', error);
      Swal.fire({
        title: 'Error',
        text: 'Could not load article data. Please check the article and PDF file exist.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
      setIsCaptureModalOpen(false);
    }
  };

  // Handle PDF page navigation
  const handlePdfPageChange = async (newPage) => {
    if (!pdfUrl) return;
    const page = Math.max(1, Math.min(numPages || Infinity, newPage));
    setPageNumber(page);
    await renderPdfPageToImage(pdfUrl, page);
  };

  // Handle the actual article capture (crop and save)
  const handleCapture = async () => {
    if (!completedCrop || !currentMatch || !pdfImageUrl || !imgRef.current) {
      Swal.fire({
        title: 'Selection Required',
        text: 'Please drag on the page to select an area to capture.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!completedCrop.width || !completedCrop.height) {
      Swal.fire({
        title: 'Selection Required',
        text: 'Please drag on the page to select an area to capture.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }
    
    setIsCapturing(true);
    
    try {
      // Convert react-image-crop coords (relative to displayed img) to pixel coords on full-res image
      const displayedImg = imgRef.current;
      const scaleX = displayedImg.naturalWidth / displayedImg.width;
      const scaleY = displayedImg.naturalHeight / displayedImg.height;
      const pixelCrop = {
        x: Math.round(completedCrop.x * scaleX),
        y: Math.round(completedCrop.y * scaleY),
        width: Math.round(completedCrop.width * scaleX),
        height: Math.round(completedCrop.height * scaleY),
      };

      const croppedImage = await getCroppedImg(pdfImageUrl, pixelCrop);
      
      // Extract just the base64 data (remove the data:image/... prefix)
      const base64Data = croppedImage.split(',')[1] || croppedImage;

      await apiClient.post('api/images/v1', {
        page_number: pageNumber,
        image_data: base64Data,
        image_format: 'png',
        image_size: Math.round(base64Data.length * 0.75 / 1024),
        image_mode: 'RGB',
        pdffile_id: currentPdffileId
      });
      
      // Ask user: crop more or mark complete?
      const result = await Swal.fire({
        title: 'Article Captured!',
        text: 'Would you like to crop another article from this PDF, or mark this match as complete?',
        icon: 'success',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Crop Another',
        denyButtonText: 'Complete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d97706',
        denyButtonColor: '#059669',
      });
      
      if (result.isConfirmed) {
        // Reset crop area for another selection
        setCrop(undefined);
        setCompletedCrop(null);
      } else {
        // "Complete" or dismissed — update match state and close
        try {
          await apiClient.put(`api/matches/v1/${currentMatch.id}`, {
            ...currentMatch,
            match_state: 2
          });
        } catch (updateError) {
          console.warn('Could not update match state:', updateError);
        }
        
        try {
          const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
          setData(refreshResponse.data);
          onDataChange?.();
        } catch (refreshError) {
          console.error('Error refreshing data:', refreshError);
        }
        
        closeCaptureModal();
      }
      
    } catch (error) {
      console.error('Error capturing article:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to capture article image',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsCapturing(false);
    }
  };

  const closeCaptureModal = () => {
    setIsCaptureModalOpen(false);
    setPdfImageUrl(null);
    setPdfUrl('');
    setCompletedCrop(null);
    setCrop(undefined);
    setCurrentMatch(null);
    setCurrentPdffileId(null);
    pdfDocRef.current = null;
  };

  // ===== IMAGE PREVIEW FUNCTIONALITY =====

  const handlePreviewImage = async (item) => {
    if (item.image_data) {
      setPreviewImageData(item.image_data);
      setPreviewTitle(`Page ${item.page_number || '?'} — ${item.image_format || 'image'}`);
      setIsPreviewOpen(true);
      return;
    }
    
    try {
      const response = await apiClient.get(`api/images/v1/${item.id}`);
      const imageRecord = response.data?.data || response.data;
      
      if (imageRecord && imageRecord.image_data) {
        setPreviewImageData(imageRecord.image_data);
        setPreviewTitle(`Page ${imageRecord.page_number || item.page_number || '?'} — ${imageRecord.image_format || item.image_format || 'image'}`);
        setIsPreviewOpen(true);
      } else {
        Swal.fire({
          title: 'No Preview',
          text: 'No image data available for this item.',
          icon: 'info',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error fetching image for preview:', error);
      Swal.fire({
        title: 'Error',
        text: 'Could not load image preview.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setPreviewImageData(null);
    setPreviewTitle('');
  };

  // ===== TABLE FUNCTIONALITY =====

  const filteredData = data.filter((item) =>
    Object.entries(item).some(([key, value]) => {
      if (key.endsWith('_id') && foreignKeyData[key]) {
        const name = getForeignKeyName(key, value);
        return String(name).toLowerCase().includes(searchTerm.toLowerCase());
      }
      return (
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    })
  );

  const indexOfLastItem = itemsPerPage === 'all' ? filteredData.length : currentPage * itemsPerPage;
  const indexOfFirstItem = itemsPerPage === 'all' ? 0 : (currentPage - 1) * itemsPerPage;
  const currentItems = itemsPerPage === 'all' 
    ? filteredData 
    : filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredData.length / itemsPerPage);

  const handleTablePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    const editData = { ...item };
    booleanColumns.forEach(col => {
      if (col in editData) {
        editData[col] = displayBooleanValue(editData[col]);
      }
    });
    setFormData(editData);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setSelectedFile(null);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let dataToSend;
      let response;
      
      if (editingId) {
        dataToSend = { ...formData };
        booleanColumns.forEach(col => {
          if (col in dataToSend) {
            dataToSend[col] = parseBooleanInput(dataToSend[col]);
          }
        });
        
        response = await apiClient.put(`api/${endpoint}/v1/${editingId}`, dataToSend);
        setData(data.map(item => item.id === editingId ? response.data : item));
        onDataChange?.();
      } else if (isAdding) {
        dataToSend = { ...newItem };
        booleanColumns.forEach(col => {
          if (col in dataToSend) {
            dataToSend[col] = parseBooleanInput(dataToSend[col]);
          }
        });
        
        readOnlyColumns.forEach(col => {
          delete dataToSend[col];
        });
        
        if (currentTab === 'pdffiles' && selectedFile) {
          const uploadFormData = new FormData();
          
          if (!selectedFile.type.includes('pdf')) {
            await Swal.fire({ title: 'Invalid File', text: 'Please upload a PDF file', icon: 'error', confirmButtonText: 'OK' });
            return;
          }
          
          if (selectedFile.size > 50 * 1024 * 1024) {
            await Swal.fire({ title: 'File Too Large', text: 'Maximum file size is 50MB', icon: 'error', confirmButtonText: 'OK' });
            return;
          }
          
          uploadFormData.append('file', selectedFile);
          
          Object.keys(dataToSend).forEach(key => {
            if (key !== 'file_encoded') {
              uploadFormData.append(key, dataToSend[key]);
            }
          });
          
          response = await apiClient.post(`api/pdffiles/upload/v1`, uploadFormData, {
            transformRequest: (data) => data,
          });
        } else {
          response = await apiClient.post(`api/${endpoint}/v1`, dataToSend);
        }
        
        setData([...data, response.data]);
        onDataChange?.();
      }
  
      if (response?.data?.message) {
        await Swal.fire({ title: 'Success', text: response.data.message, icon: 'success', confirmButtonText: 'OK' });
      }
      
      try {
        const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
        setData(refreshResponse.data);
        onDataChange?.();
      } catch (refreshError) {
        console.error('Error refreshing data:', refreshError);
      }
      
    } catch (error) {
      console.error('Error saving data:', error);
      const errorMsg = error.response?.data?.message || 'An error occurred while saving the data';
      await Swal.fire({ title: 'Error', text: errorMsg, icon: 'error', confirmButtonText: 'OK' });
    } finally {
      setIsLoading(false);
      setEditingId(null);
      setIsAdding(false);
      setSelectedFile(null);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Confirm Delete', text: 'Are you sure you want to delete this item?',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Yes, delete it', cancelButtonText: 'Cancel'
    });
    
    if (!result.isConfirmed) return;
    
    setIsLoading(true);
    try {
      await apiClient.delete(`api/${endpoint}/v1/${id}`);
      setData(data.filter(item => item.id !== id));
      onDataChange?.();
      await Swal.fire({ title: 'Deleted', text: 'Item deleted successfully', icon: 'success', confirmButtonText: 'OK' });
      try {
        const refreshResponse = await apiClient.get(`api/${endpoint}/v1`);
        setData(refreshResponse.data);
        onDataChange?.();
      } catch (refreshError) {
        console.error('Error refreshing data:', refreshError);
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      await Swal.fire({ title: 'Error', text: error.response?.data?.message || 'Failed to delete item', icon: 'error', confirmButtonText: 'OK' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (editingId) {
      setFormData({ ...formData, [name]: value });
    } else if (isAdding) {
      setNewItem({ ...newItem, [name]: value });
    }
  };

  const handleAddNew = () => {
    setIsAdding(true);
    if (data.length > 0) {
      const emptyItem = {};
      Object.keys(data[0]).forEach(key => {
        emptyItem[key] = booleanColumns.includes(key) ? 'No' : '';
        if (readOnlyColumns.includes(key)) { delete emptyItem[key]; }
      });
      setNewItem(emptyItem);
    }
    setSelectedFile(null);
  };

  const renderFieldInput = (col, value, onChange, isNew = false) => {
    if (booleanColumns.includes(col)) {
      return (
        <select name={col} value={value || 'No'} onChange={onChange}>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      );
    }
    if (col === 'day_of_week') {
      return (
        <select name={col} value={value || ''} onChange={onChange}>
          <option value="">Select Day</option>
          {weekdays.map(day => (<option key={day} value={day}>{day}</option>))}
        </select>
      );
    }
    if (col.endsWith('_id') && foreignKeyData[col]) {
      if (foreignKeyErrors[col]) {
        return <div className="error-message">{foreignKeyErrors[col]}</div>;
      }
      return (
        <select name={col} value={value || ''} onChange={onChange} disabled={foreignKeyLoading}>
          <option value="">Select {col.replace('_id', '').replace(/_/g, ' ')}</option>
          {foreignKeyData[col].map(item => (
            <option key={item.id} value={item.id}>{item.name || item.title || item.label || item.id}</option>
          ))}
        </select>
      );
    }
    if (col === 'content' || col === 'description' || col === 'response_json') {
      return (<textarea name={col} value={value || ''} onChange={onChange} rows={3} disabled={readOnlyColumns.includes(col)} />);
    }
    return (
      <input
        type={col === 'password' ? 'password' : col === 'email' ? 'email' : 'text'}
        name={col} value={value || ''} onChange={onChange}
        disabled={col === 'id' || readOnlyColumns.includes(col)}
      />
    );
  };

  if (!data || data.length === 0) {
    return (
      <div className="data-table">
        <button className="btn-add" onClick={handleAddNew}>Add New</button>
        {isAdding && (
          <div className="add-form">
            <h3>Add New Item</h3>
            <p style={{ color: '#666', fontStyle: 'italic' }}>No existing data to determine fields. Please add data via the API first.</p>
            <button onClick={handleCancel}>Cancel</button>
          </div>
        )}
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No data available</div>
      </div>
    );
  }

  const allColumns = Object.keys(data[0]).filter(col => 
    !(isAdding && readOnlyColumns.includes(col)) && !hiddenColumns.includes(col)
  );

  return (
    <div className="data-table">
      <div className="search-bar">
        <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <button className="btn-add" onClick={handleAddNew}>Add New</button>
      </div>
      
      {/* Pagination Controls */}
      <div className="pagination-controls">
        <div className="items-per-page">
          <label>Items per page:</label>
          <select value={itemsPerPage} onChange={handleItemsPerPageChange} disabled={isLoading}>
            {pageSizeOptions.map(option => (
              <option key={option.value || option} value={option.value || option}>{option.label || option}</option>
            ))}
          </select>
        </div>
        {itemsPerPage !== 'all' && (
          <div className="pagination-navigation">
            <button onClick={() => handleTablePageChange(1)} disabled={currentPage === 1 || isLoading}>First</button>
            <button onClick={() => handleTablePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading}>Previous</button>
            <span className="page-info">Page {currentPage} of {totalPages}</span>
            <button onClick={() => handleTablePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading}>Next</button>
            <button onClick={() => handleTablePageChange(totalPages)} disabled={currentPage === totalPages || isLoading}>Last</button>
          </div>
        )}
      </div>
      
      {/* Add New Item Form */}
      {isAdding && (
        <div className="add-form">
          <h3>Add New Item</h3>
          <div className="form-fields">
            {currentTab === 'pdffiles' && (
              <div className="form-field">
                <label>Upload PDF:</label>
                <input type="file" accept=".pdf" onChange={handleFileChange} />
              </div>
            )}
            {allColumns.map((col) => (
              <div key={`new-${col}`} className="form-field">
                <label>{col.replace(/_/g, ' ')}:</label>
                {renderFieldInput(col, newItem[col], handleChange, true)}
              </div>
            ))}
          </div>
          <div className="form-actions">
            <button onClick={handleSave} disabled={isLoading}>{isLoading ? 'Saving...' : 'Save'}</button>
            <button onClick={handleCancel} disabled={isLoading}>Cancel</button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="table-scroll-wrapper">
        <table>
          <thead>
            <tr>
              {displayColumns.map((col) => (
                <th key={col}>{col.endsWith('_id') ? col.replace('_id', '').replace(/_/g, ' ') : col.replace(/_/g, ' ')}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((item) => (
              <tr key={item.id}>
                {displayColumns.map((col) => (
                  <td key={`${item.id}-${col}`}>
                    {editingId === item.id ? (
                      readOnlyColumns.includes(col) ? (
                        <span className="readonly-value">{item[col]}</span>
                      ) : (
                        renderFieldInput(col, formData[col], handleChange)
                      )
                    ) : (
                      booleanColumns.includes(col) 
                        ? displayBooleanValue(item[col]) 
                        : col.endsWith('_id') 
                          ? getForeignKeyName(col, item[col]) 
                          : (String(item[col] || '').length > 80 
                              ? String(item[col]).substring(0, 80) + '...' 
                              : item[col])
                    )}
                  </td>
                ))}
                <td className="actions-cell">
                  {editingId === item.id ? (
                    <>
                      <button className="btn-save" onClick={handleSave} disabled={isLoading}>{isLoading ? 'Saving...' : 'Save'}</button>
                      <button className="btn-cancel" onClick={handleCancel} disabled={isLoading}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <button className="btn-edit" onClick={() => handleEdit(item)} disabled={isLoading || isAdding}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(item.id)} disabled={isLoading || isAdding}>Delete</button>
                      {currentTab === 'matches' && (
                        <button 
                          className="btn-capture"
                          onClick={() => handleCaptureArticle(item)}
                          disabled={isLoading || isAdding || item.match_state === 2 || item.match_state === '2'}
                          title={item.match_state === 2 || item.match_state === '2' ? 'Already captured' : 'Capture article image'}
                        >
                          {item.match_state === 2 || item.match_state === '2' ? 'Captured' : 'Capture'}
                        </button>
                      )}
                      {currentTab === 'images' && (
                        <button 
                          className="btn-preview btn-capture"
                          onClick={() => handlePreviewImage(item)}
                          disabled={isLoading || isAdding}
                          title="Preview image"
                        >
                          View
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Bottom pagination */}
      {itemsPerPage !== 'all' && (
        <div className="pagination-controls bottom">
          <div className="pagination-navigation">
            <button onClick={() => handleTablePageChange(1)} disabled={currentPage === 1 || isLoading}>First</button>
            <button onClick={() => handleTablePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading}>Previous</button>
            <span className="page-info">Page {currentPage} of {totalPages} (Showing {currentItems.length} of {filteredData.length} items)</span>
            <button onClick={() => handleTablePageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading}>Next</button>
            <button onClick={() => handleTablePageChange(totalPages)} disabled={currentPage === totalPages || isLoading}>Last</button>
          </div>
        </div>
      )}

      {/* ===== Capture Article Modal ===== */}
      <Modal
        isOpen={isCaptureModalOpen}
        onRequestClose={closeCaptureModal}
        contentLabel="Capture Article"
        className="capture-modal"
        overlayClassName="capture-overlay"
      >
        <div className="capture-modal-header">
          <h2>Capture Article Image</h2>
          <button className="modal-close-btn" onClick={closeCaptureModal}>&times;</button>
        </div>
        
        <div className="pdf-capture-container">
          {/* PDF Page Navigation */}
          <div className="page-controls">
            <button 
              onClick={() => handlePdfPageChange(pageNumber - 1)}
              disabled={pageNumber <= 1 || isPdfLoading}
              className="page-nav-btn"
            >
              &larr; Previous Page
            </button>
            <span className="page-indicator">
              Page {pageNumber} of {numPages || '...'}
            </span>
            <button 
              onClick={() => handlePdfPageChange(pageNumber + 1)}
              disabled={pageNumber >= (numPages || Infinity) || isPdfLoading}
              className="page-nav-btn"
            >
              Next Page &rarr;
            </button>
          </div>

          {/* Cropping Area — drag to draw a resizable selection box */}
          <div className="crop-area-wrapper">
            {isPdfLoading ? (
              <div className="loading-message">
                <div className="spinner"></div>
                Rendering PDF page...
              </div>
            ) : pdfImageUrl ? (
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
              >
                <img
                  ref={imgRef}
                  src={pdfImageUrl}
                  alt="PDF page"
                  style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', margin: '0 auto' }}
                  onLoad={() => {
                    // Reset crop when image loads
                    setCrop(undefined);
                    setCompletedCrop(null);
                  }}
                />
              </ReactCrop>
            ) : (
              <div className="loading-message">
                No PDF page loaded. Please check the file.
              </div>
            )}
          </div>

          {/* Capture Controls */}
          <div className="capture-controls">
            <div style={{ fontSize: '0.85rem', color: '#666' }}>
              Drag on the page to select the area you want to capture
            </div>
            
            <div className="action-buttons">
              <button 
                onClick={handleCapture} 
                disabled={isCapturing || !pdfImageUrl || !completedCrop || !completedCrop.width}
                className="capture-button"
              >
                {isCapturing ? 'Capturing...' : 'Save Captured Area'}
              </button>
              <button 
                onClick={closeCaptureModal} 
                disabled={isCapturing}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* ===== Image Preview Modal ===== */}
      {isPreviewOpen && (
        <div className="preview-overlay" onClick={closePreview}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>{previewTitle}</h3>
              <button className="modal-close-btn" onClick={closePreview}>&times;</button>
            </div>
            <div className="preview-modal-body">
              {previewImageData && (
                <img
                  src={
                    previewImageData.startsWith('data:')
                      ? previewImageData
                      : `data:image/png;base64,${previewImageData}`
                  }
                  alt={previewTitle}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;